
import { useState, useCallback, useRef, useEffect } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import { addFinanceRecord, dispatchFinanceUpdate } from '../../store/financeStore';
import type { FinanceRecord } from '../../store/financeStore';
import { useCustomers } from '../../store/customerStore';
import type { Customer } from '../../store/customerStore';

type TabType = 'purchase' | 'sale';

interface FormData {
  date: string;
  variety: string;
  quantity: string;
  unit: string;
  price: string;
  amount: string;
  settlement: string;
  nature: string;
  source: string;
  customerId: number | null;
  contact: string;
  phone: string;
  idCard: string;
  moisture: string;
  impurity: string;
  incomplete: string;
  remark: string;
  operatingCost: string;
  taxRate: string;
  taxAmount: string;
  subsidyAmount: string;
  subsidyCategory: string;
}

const defaultForm: FormData = {
  date: new Date().toISOString().split('T')[0],
  variety: '',
  quantity: '',
  unit: '吨',
  price: '',
  amount: '',
  settlement: '现金',
  nature: '商品粮',
  source: '',
  customerId: null,
  contact: '',
  phone: '',
  idCard: '',
  moisture: '',
  impurity: '',
  incomplete: '',
  remark: '',
  operatingCost: '',
  taxRate: '3',
  taxAmount: '',
  subsidyAmount: '',
  subsidyCategory: '',
};

interface ToastProps {
  message: string;
  type: 'success' | 'error';
}

function Toast({ message, type }: ToastProps) {
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-white text-sm font-medium animate-fade-in-down
        ${type === 'success' ? 'bg-emerald-500' : 'bg-rose-500'}`}
    >
      <i className={`text-lg ${type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}`}></i>
      {message}
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  badge?: string;
  badgeColor?: string;
}

function FormSection({ title, icon, children, collapsible = false, defaultOpen = true, badge, badgeColor }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm mb-5 overflow-hidden">
      <div
        className={`flex items-center justify-between px-6 py-4 border-b border-gray-100 ${collapsible ? 'cursor-pointer select-none hover:bg-gray-50 transition-colors' : ''}`}
        onClick={collapsible ? () => setOpen(v => !v) : undefined}
      >
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
            <i className={`${icon} text-base`}></i>
          </span>
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          {badge && (
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${badgeColor || 'bg-rose-50 text-rose-500'}`}>
              {badge}
            </span>
          )}
        </div>
        {collapsible && (
          <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
        )}
      </div>
      {open && <div className="p-6">{children}</div>}
    </div>
  );
}

interface InputFieldProps {
  label: string;
  required?: boolean;
  icon?: string;
  children: React.ReactNode;
  hint?: string;
}

function InputField({ label, required, icon, children, hint }: InputFieldProps) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
        {icon && <i className={`${icon} mr-1 text-gray-400`}></i>}
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-300 mt-1">{hint}</p>}
    </div>
  );
}

// ===== 下拉搜索选择组件 =====
interface CustomerSearchSelectProps {
  customers: Customer[];
  value: string;
  customerId: number | null;
  placeholder: string;
  required?: boolean;
  onChange: (customer: Customer | null, inputValue: string) => void;
}

function CustomerSearchSelect({ customers, value, customerId, placeholder, required, onChange }: CustomerSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  const filtered = customers.filter(c =>
    c.name.includes(search) || c.contact.includes(search) || c.phone.includes(search)
  );

  // 点击外部关闭
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (c: Customer) => {
    onChange(c, c.name);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, '');
    setSearch('');
  };

  const displayValue = customerId ? value : value;

  return (
    <div ref={wrapRef} className="relative">
      <div
        className={`flex items-center w-full px-3.5 py-2.5 border rounded-lg text-sm bg-white transition-all cursor-pointer
          ${open ? 'border-teal-400 ring-2 ring-teal-400' : 'border-gray-200 hover:border-gray-300'}`}
        onClick={() => { setOpen(v => !v); setSearch(''); }}
      >
        {customerId ? (
          <span className="flex-1 text-gray-800 truncate">{displayValue}</span>
        ) : (
          <span className="flex-1 text-gray-300 truncate">{value || placeholder}</span>
        )}
        <div className="flex items-center gap-1 ml-2 shrink-0">
          {(customerId || value) && (
            <span
              className="w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer"
              onClick={handleClear}
            >
              <i className="ri-close-line text-sm"></i>
            </span>
          )}
          <span className="w-4 h-4 flex items-center justify-center text-gray-400">
            <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? 'rotate-180' : ''}`}></i>
          </span>
        </div>
      </div>

      {/* 隐藏的 required 校验 input */}
      {required && (
        <input
          tabIndex={-1}
          required
          value={value}
          onChange={() => {}}
          className="absolute inset-0 opacity-0 pointer-events-none w-full"
        />
      )}

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {/* 搜索框 */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <i className="ri-search-line text-gray-400 text-sm"></i>
              <input
                autoFocus
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="搜索名称、联系人、电话..."
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-300"
                onClick={e => e.stopPropagation()}
              />
            </div>
          </div>

          {/* 列表 */}
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-300">
                <i className="ri-user-search-line text-2xl block mb-1"></i>
                未找到匹配的{customers[0]?.type === '供应商' ? '供应商' : '客户'}
              </div>
            ) : (
              filtered.map(c => (
                <div
                  key={c.id}
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-teal-50 transition-colors
                    ${c.id === customerId ? 'bg-teal-50' : ''}`}
                  onClick={() => handleSelect(c)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 text-xs font-bold shrink-0">
                      {c.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-800 flex items-center gap-1.5">
                        {c.name}
                        {c.id === customerId && <i className="ri-check-line text-teal-500 text-xs"></i>}
                      </div>
                      <div className="text-xs text-gray-400">{c.contact} · {c.phone}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-300 shrink-0 ml-2">{c.address}</div>
                </div>
              ))
            )}
          </div>

          {/* 底部提示 */}
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-300 flex items-center gap-1">
            <i className="ri-information-line"></i>
            共 {filtered.length} 条，可在客户管理中维护数据
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  'w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm text-gray-800 bg-white transition-all placeholder-gray-300';
const selectCls =
  'w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-400 focus:border-transparent text-sm text-gray-800 bg-white transition-all cursor-pointer';

let nextId = Date.now();

export default function Entry() {
  const [activeTab, setActiveTab] = useState<TabType>('purchase');
  const [formData, setFormData] = useState<FormData>(defaultForm);
  const [toast, setToast] = useState<ToastProps | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { suppliers, buyers } = useCustomers();

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData(prev => {
        const updated = { ...prev, [name]: value };

        if (name === 'quantity' || name === 'price') {
          const qty = parseFloat(name === 'quantity' ? value : updated.quantity) || 0;
          const price = parseFloat(name === 'price' ? value : updated.price) || 0;
          updated.amount = qty > 0 && price > 0 ? (qty * price).toFixed(2) : '';
        }

        if (name === 'amount' || name === 'quantity' || name === 'price') {
          const amt = parseFloat(updated.amount) || 0;
          if (activeTab === 'sale') {
            if (!updated.operatingCost) updated.operatingCost = amt > 0 ? (amt * 0.98).toFixed(2) : '';
            const rate = parseFloat(updated.taxRate) || 0;
            updated.taxAmount = amt > 0 && rate > 0 ? (amt * rate / 100).toFixed(2) : '';
          }
        }

        if (name === 'taxRate') {
          const amt = parseFloat(updated.amount) || 0;
          const rate = parseFloat(value) || 0;
          updated.taxAmount = amt > 0 && rate > 0 ? (amt * rate / 100).toFixed(2) : '';
        }

        return updated;
      });
    },
    [activeTab]
  );

  // 客户选择回调
  const handleCustomerSelect = useCallback((customer: Customer | null, inputValue: string) => {
    setFormData(prev => ({
      ...prev,
      source: inputValue,
      customerId: customer ? customer.id : null,
      contact: customer ? customer.contact : '',
      phone: customer ? customer.phone : '',
      idCard: customer ? (customer.idCard || '') : '',
    }));
  }, []);

  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
    setFormData(prev => ({
      ...prev,
      source: '',
      customerId: null,
      contact: '',
      phone: '',
      idCard: '',
      operatingCost: '',
      taxRate: tab === 'sale' ? '3' : '0',
      taxAmount: '',
      subsidyAmount: '',
      subsidyCategory: '',
    }));
  }, []);

  const handleReset = useCallback(() => {
    setFormData({ ...defaultForm, date: new Date().toISOString().split('T')[0] });
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      setTimeout(() => {
        const qty = parseFloat(formData.quantity) || 0;
        const price = parseFloat(formData.price) || 0;
        const amount = parseFloat(formData.amount) || 0;
        const operatingCost = parseFloat(formData.operatingCost) || (activeTab === 'purchase' ? amount : amount * 0.98);
        const taxRate = parseFloat(formData.taxRate) || 0;
        const taxAmount = parseFloat(formData.taxAmount) || 0;
        const subsidyAmount = parseFloat(formData.subsidyAmount) || 0;

        const record: FinanceRecord = {
          id: ++nextId,
          date: formData.date,
          bizType: activeTab === 'purchase' ? '收购' : '销售',
          variety: formData.variety,
          quantity: qty,
          unit: formData.unit,
          unitPrice: price,
          amount,
          revenue: activeTab === 'sale' ? amount : 0,
          cost: operatingCost,
          grossProfit: activeTab === 'sale' ? amount - operatingCost : 0,
          settlement: formData.settlement as FinanceRecord['settlement'],
          tax: taxAmount,
          taxRate,
          subsidy: subsidyAmount,
          subsidyType: formData.subsidyCategory,
          counterpart: formData.source,
          remark: formData.remark,
          operatingCost,
          taxAmount,
          subsidyAmount,
          subsidyCategory: formData.subsidyCategory,
          nature: formData.nature,
          source: formData.source,
          // 台账 & 客户关联字段
          customerId: formData.customerId ?? undefined,
          contact: formData.contact,
          phone: formData.phone,
          idCard: formData.idCard,
          moisture: formData.moisture,
          impurity: formData.impurity,
          incomplete: formData.incomplete,
        };

        addFinanceRecord(record);
        dispatchFinanceUpdate();

        setSubmitting(false);
        showToast(`${activeTab === 'purchase' ? '收购' : '销售'}记录保存成功，已同步至财务管理！`, 'success');
        handleReset();
      }, 800);
    },
    [activeTab, formData, showToast, handleReset]
  );

  const amountValue = parseFloat(formData.amount) || 0;
  const taxAmountValue = parseFloat(formData.taxAmount) || 0;
  const subsidyAmountValue = parseFloat(formData.subsidyAmount) || 0;
  const operatingCostValue = parseFloat(formData.operatingCost) || 0;
  const grossProfit = activeTab === 'sale' ? amountValue - operatingCostValue - taxAmountValue : 0;

  // 当前 tab 对应的客户列表
  const currentCustomerList = activeTab === 'purchase' ? suppliers : buyers;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      {toast && <Toast message={toast.message} type={toast.type} />}

      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        {/* 顶部标题栏 */}
        <div className="bg-white border-b border-gray-100 px-8 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="text-xl font-bold text-gray-900">快速录入</h1>
            <p className="text-xs text-gray-400 mt-0.5">记录粮食收购与销售业务，确保台账合规</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-shield-check-line text-teal-500"></i>
            符合《粮食经营台账管理办法》第六、七条
          </div>
        </div>

        <div className="flex-1 p-8 pb-28">
          {/* 胶囊式标签页 */}
          <div className="flex items-center gap-1 bg-white border border-gray-100 rounded-xl p-1 shadow-sm w-fit mb-6">
            <button
              type="button"
              onClick={() => handleTabChange('purchase')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'purchase' ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-arrow-down-circle-line text-base"></i>
              收购记录
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('sale')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === 'sale' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-arrow-up-circle-line text-base"></i>
              销售记录
            </button>
          </div>

          <form id="entry-form" onSubmit={handleSubmit}>
            {/* 基础信息 */}
            <FormSection title="基础信息" icon="ri-file-list-3-line" badge="必填">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <InputField label="业务日期" required icon="ri-calendar-line">
                  <input type="date" name="date" value={formData.date} onChange={handleChange} required className={inputCls} />
                </InputField>
                <InputField label="粮食品种" required icon="ri-seedling-line">
                  <select name="variety" value={formData.variety} onChange={handleChange} required className={selectCls}>
                    <option value="">请选择品种</option>
                    {['小麦', '玉米', '大米', '大豆', '高粱', '稻谷', '其他'].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </InputField>
                <InputField label="粮食性质" required icon="ri-price-tag-3-line">
                  <select name="nature" value={formData.nature} onChange={handleChange} required className={selectCls}>
                    <option value="商品粮">商品粮</option>
                    <option value="政策性粮食">政策性粮食</option>
                    <option value="其他">其他</option>
                  </select>
                </InputField>
                <InputField label="结算方式" required icon="ri-bank-card-line">
                  <select name="settlement" value={formData.settlement} onChange={handleChange} required className={selectCls}>
                    {['现金', '转账', '支票', '承兑', '赊账'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </InputField>
              </div>
            </FormSection>

            {/* 数量与金额 */}
            <FormSection title="数量与金额" icon="ri-scales-3-line" badge="必填">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
                <InputField label="数量" required icon="ri-stack-line">
                  <div className="flex gap-2">
                    <input type="number" name="quantity" value={formData.quantity} onChange={handleChange} required step="0.01" min="0" placeholder="0.00" className={`${inputCls} flex-1`} />
                    <select name="unit" value={formData.unit} onChange={handleChange} className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-teal-400 focus:border-transparent cursor-pointer w-20">
                      <option value="吨">吨</option>
                      <option value="千克">千克</option>
                      <option value="斤">斤</option>
                    </select>
                  </div>
                </InputField>
                <InputField label="单价（元）" required icon="ri-money-cny-circle-line">
                  <input type="number" name="price" value={formData.price} onChange={handleChange} required step="0.01" min="0" placeholder="0.00" className={inputCls} />
                </InputField>
                <InputField label="金额（元）" icon="ri-calculator-line">
                  <div className={`relative ${amountValue > 0 ? 'ring-2 ring-teal-300 rounded-lg' : ''}`}>
                    <input type="number" name="amount" value={formData.amount} readOnly placeholder="自动计算" className={`${inputCls} bg-teal-50/60 text-teal-700 font-semibold cursor-not-allowed`} />
                    {amountValue > 0 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-teal-500 font-medium">
                        ≈ {(amountValue / 10000).toFixed(2)}万
                      </span>
                    )}
                  </div>
                </InputField>
              </div>
              {amountValue > 0 && (
                <div className="flex items-center gap-3 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 rounded-xl px-5 py-3.5">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl"></i>
                  <div>
                    <span className="text-xs text-teal-600 font-medium">本笔业务金额</span>
                    <span className="ml-3 text-2xl font-bold text-teal-700">
                      ¥ {Number(formData.amount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="ml-auto text-xs text-teal-500">
                    {formData.quantity} {formData.unit} × ¥{formData.price}/吨
                  </div>
                </div>
              )}
            </FormSection>

            {/* 来源/客户信息 */}
            <FormSection title={activeTab === 'purchase' ? '来源信息' : '客户信息'} icon="ri-user-3-line" badge="必填">
              {/* 提示条 */}
              <div className="flex items-center gap-2 mb-4 px-3.5 py-2.5 bg-teal-50 border border-teal-100 rounded-lg text-xs text-teal-600">
                <i className="ri-links-line text-sm"></i>
                从客户管理中选择{activeTab === 'purchase' ? '供应商' : '客户'}，可自动填充联系信息；也可手动输入新的名称
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* 下拉搜索选择 */}
                <InputField
                  label={activeTab === 'purchase' ? '生产者/企业名称' : '客户名称'}
                  required
                  icon="ri-building-line"
                >
                  <CustomerSearchSelect
                    customers={currentCustomerList}
                    value={formData.source}
                    customerId={formData.customerId}
                    placeholder={activeTab === 'purchase' ? '搜索或选择供应商' : '搜索或选择客户'}
                    required
                    onChange={handleCustomerSelect}
                  />
                </InputField>

                {/* 联系人 — 选择后自动填充，可手动修改 */}
                <InputField label="联系人" icon="ri-contacts-line">
                  <div className="relative">
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      placeholder="请输入联系人"
                      className={`${inputCls} ${formData.customerId ? 'bg-teal-50/40' : ''}`}
                    />
                    {formData.customerId && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-links-line text-teal-400 text-xs"></i>
                      </span>
                    )}
                  </div>
                </InputField>

                {/* 联系电话 — 选择后自动填充，可手动修改 */}
                <InputField label="联系电话" icon="ri-phone-line">
                  <div className="relative">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="请输入联系电话"
                      className={`${inputCls} ${formData.customerId ? 'bg-teal-50/40' : ''}`}
                    />
                    {formData.customerId && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                        <i className="ri-links-line text-teal-400 text-xs"></i>
                      </span>
                    )}
                  </div>
                </InputField>

                {activeTab === 'purchase' && (
                  <InputField label="身份证号" icon="ri-id-card-line">
                    <input
                      type="text"
                      name="idCard"
                      value={formData.idCard}
                      onChange={handleChange}
                      placeholder="生产者身份证号"
                      className={`${inputCls} ${formData.customerId && formData.idCard ? 'bg-teal-50/40' : ''}`}
                    />
                  </InputField>
                )}
              </div>
            </FormSection>

            {/* 财务信息 */}
            <FormSection
              title="财务信息"
              icon="ri-money-cny-box-line"
              badge="自动同步至财务管理"
              badgeColor="bg-amber-50 text-amber-600"
              collapsible
              defaultOpen
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <InputField
                  label={activeTab === 'purchase' ? '采购成本（元）' : '营业成本（元）'}
                  icon="ri-shopping-cart-2-line"
                  hint={activeTab === 'sale' ? '销售业务的直接成本，如收购价、运费等' : '本次收购的实际支出成本'}
                >
                  <input
                    type="number"
                    name="operatingCost"
                    value={formData.operatingCost}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder={amountValue > 0 ? `参考金额 ¥${amountValue.toLocaleString()}` : '0.00'}
                    className={inputCls}
                  />
                </InputField>

                {activeTab === 'sale' && (
                  <>
                    <InputField label="增值税率（%）" icon="ri-percent-line" hint="小规模纳税人适用3%，一般纳税人适用9%">
                      <select name="taxRate" value={formData.taxRate} onChange={handleChange} className={selectCls}>
                        <option value="0">免税 / 0%</option>
                        <option value="3">小规模 3%</option>
                        <option value="9">一般纳税人 9%</option>
                        <option value="13">标准税率 13%</option>
                      </select>
                    </InputField>
                    <InputField label="应缴税款（元）" icon="ri-government-line" hint="根据销售金额和税率自动计算">
                      <div className="relative">
                        <input
                          type="number"
                          name="taxAmount"
                          value={formData.taxAmount}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          placeholder="自动计算或手动填写"
                          className={`${inputCls} ${taxAmountValue > 0 ? 'bg-rose-50/50 text-rose-600' : ''}`}
                        />
                        {taxAmountValue > 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-rose-400">
                            税率 {formData.taxRate}%
                          </span>
                        )}
                      </div>
                    </InputField>
                  </>
                )}

                {activeTab === 'purchase' && (
                  <>
                    <InputField label="政府补贴金额（元）" icon="ri-hand-coin-line" hint="本次收购获得的政府补贴，无则留空">
                      <input
                        type="number"
                        name="subsidyAmount"
                        value={formData.subsidyAmount}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="0.00（无补贴可留空）"
                        className={`${inputCls} ${subsidyAmountValue > 0 ? 'bg-indigo-50/50 text-indigo-600' : ''}`}
                      />
                    </InputField>
                    <InputField label="补贴类型" icon="ri-file-list-check-line">
                      <select name="subsidyCategory" value={formData.subsidyCategory} onChange={handleChange} className={selectCls}>
                        <option value="">无补贴</option>
                        <option value="粮食收购补贴">粮食收购补贴</option>
                        <option value="优质粮食工程补贴">优质粮食工程补贴</option>
                        <option value="农机购置补贴">农机购置补贴</option>
                        <option value="粮食储备补贴">粮食储备补贴</option>
                        <option value="其他补贴">其他补贴</option>
                      </select>
                    </InputField>
                  </>
                )}
              </div>

              {amountValue > 0 && (
                <div className="mt-5 grid grid-cols-4 gap-3">
                  <div className="bg-teal-50 rounded-xl px-4 py-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">{activeTab === 'sale' ? '营业收入' : '采购金额'}</div>
                    <div className="text-base font-bold text-teal-600">¥{amountValue.toLocaleString()}</div>
                  </div>
                  <div className="bg-amber-50 rounded-xl px-4 py-3 text-center">
                    <div className="text-xs text-gray-400 mb-1">{activeTab === 'sale' ? '营业成本' : '采购成本'}</div>
                    <div className="text-base font-bold text-amber-600">
                      {operatingCostValue > 0 ? `¥${operatingCostValue.toLocaleString()}` : '—'}
                    </div>
                  </div>
                  {activeTab === 'sale' ? (
                    <>
                      <div className="bg-rose-50 rounded-xl px-4 py-3 text-center">
                        <div className="text-xs text-gray-400 mb-1">应缴税款</div>
                        <div className="text-base font-bold text-rose-500">
                          {taxAmountValue > 0 ? `¥${taxAmountValue.toLocaleString()}` : '—'}
                        </div>
                      </div>
                      <div className={`rounded-xl px-4 py-3 text-center ${grossProfit >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                        <div className="text-xs text-gray-400 mb-1">税后净利润</div>
                        <div className={`text-base font-bold ${grossProfit >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                          {operatingCostValue > 0 ? `${grossProfit >= 0 ? '+' : ''}¥${grossProfit.toLocaleString()}` : '—'}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-indigo-50 rounded-xl px-4 py-3 text-center">
                        <div className="text-xs text-gray-400 mb-1">政府补贴</div>
                        <div className="text-base font-bold text-indigo-500">
                          {subsidyAmountValue > 0 ? `¥${subsidyAmountValue.toLocaleString()}` : '—'}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl px-4 py-3 text-center">
                        <div className="text-xs text-gray-400 mb-1">实际成本</div>
                        <div className="text-base font-bold text-gray-600">
                          {operatingCostValue > 0 ? `¥${(operatingCostValue - subsidyAmountValue).toLocaleString()}` : '—'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </FormSection>

            {/* 质量指标（可折叠） */}
            <FormSection title="质量指标" icon="ri-flask-line" collapsible defaultOpen={false} badge="可选" badgeColor="bg-gray-100 text-gray-400">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <InputField label="水分（%）" icon="ri-drop-line">
                  <input type="number" name="moisture" value={formData.moisture} onChange={handleChange} step="0.1" min="0" max="100" placeholder="如：13.5" className={inputCls} />
                </InputField>
                <InputField label="杂质（%）" icon="ri-filter-line">
                  <input type="number" name="impurity" value={formData.impurity} onChange={handleChange} step="0.1" min="0" max="100" placeholder="如：1.0" className={inputCls} />
                </InputField>
                <InputField label="不完善粒（%）" icon="ri-alert-line">
                  <input type="number" name="incomplete" value={formData.incomplete} onChange={handleChange} step="0.1" min="0" max="100" placeholder="如：5.0" className={inputCls} />
                </InputField>
              </div>
            </FormSection>

            {/* 备注（可折叠） */}
            <FormSection title="备注信息" icon="ri-sticky-note-line" collapsible defaultOpen={false} badge="可选" badgeColor="bg-gray-100 text-gray-400">
              <textarea
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="可填写其他补充说明..."
                className={`${inputCls} resize-none`}
              />
              <div className="text-right text-xs text-gray-300 mt-1">{formData.remark.length}/500</div>
            </FormSection>
          </form>
        </div>

        {/* 固定吸底操作栏 */}
        <div className="fixed bottom-0 left-64 right-0 bg-white border-t border-gray-100 shadow-lg px-8 py-4 z-20">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置表单
            </button>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-teal-600 bg-teal-50 px-3 py-2 rounded-lg">
                <i className="ri-links-line"></i>
                保存后自动同步至财务管理 &amp; 台账
              </div>
              <button
                type="button"
                className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-upload-2-line"></i>
                批量导入
              </button>
              <button
                type="submit"
                form="entry-form"
                disabled={submitting}
                className={`flex items-center gap-2 px-8 py-2.5 rounded-lg text-sm font-semibold text-white transition-all whitespace-nowrap cursor-pointer shadow-sm ${
                  activeTab === 'purchase' ? 'bg-teal-600 hover:bg-teal-700 shadow-teal-200' : 'bg-orange-500 hover:bg-orange-600 shadow-orange-200'
                } ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i>保存中...</>
                ) : (
                  <><i className="ri-save-line"></i>保存记录</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
